import { ChangeDetectorRef, ElementRef, EventEmitter, QueryList, Renderer2, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { isActiveRouterLinkFactory } from '@core/frontend-shared';
import { arraySplitByCondition } from '@core/shared/utils';
import { from, of } from 'rxjs';
import { mergeAll } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@angular/router";
/**
 * RouterLinkActivePath is a replacement for RouterLinkActive.
 * It is able to set different classes for exact matches [active] and for other matches [inActivePath].
 * Additionally, the detection of state works different to RouterLinkActive:
 * RouterLinks found in Content Dom will only be used if routerLinkActivePath is set on an element without a routerLink.
 * Otherwise, state will be calculated by comparing the current router tree with the tree of the routerLink.
 * Most logic is copied from RouterLinkActive directive.
 * https://github.com/angular/angular/blob/main/packages/router/src/directives/router_link_active.ts
 */
export let UiRouterLinkActivePathDirective = /*#__PURE__*/(() => {
  class UiRouterLinkActivePathDirective {
    get isActive() {
      return this._isActive;
    }
    get isInActivePath() {
      return this._isInActivePath;
    }
    // identical to regular routerLinkActive
    constructor(router, renderer, element, cdr, link) {
      this.router = router;
      this.renderer = renderer;
      this.element = element;
      this.cdr = cdr;
      this.link = link;
      this.stateEmitter = new EventEmitter();
      this.routerLink = null;
      this._isActive = false;
      // custom properties
      this._isInActivePath = false;
      this.routerLink = link || null;
      this.routerEventsSubscription = router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.update();
        }
      });
    }
    // identical to regular routerLinkActive
    ngOnChanges(changes) {
      this.update();
    }
    // added this.linksWithHrefs.changes
    ngAfterContentInit() {
      // `of(null)` is used to force subscribe body to execute once immediately (like `startWith`).
      of(this.links.changes, this.linksWithHrefs.changes, of(null)).pipe(mergeAll()).subscribe(_ => {
        this.update();
        this.subscribeToEachLinkOnChanges();
      });
    }
    // accesses link.onChanges which according to compiler does not exist.
    // remove call to this.isLinkActive, use isActiveRouterLinkFactory instead
    subscribeToEachLinkOnChanges() {
      this.linkInputChangesSubscription?.unsubscribe();
      const allLinkChanges = [...this.links.toArray(), this.link].filter(link => !!link)
      // onChanges is a BehaviorSubject marked with @internal.
      // it does not even appear as private prop in declaration. 
      // need to cast type to any to access it.
      .map(link => link.onChanges);
      this.linkInputChangesSubscription = from(allLinkChanges).pipe(mergeAll()).subscribe(link => {
        if (this._isActive !== isActiveRouterLinkFactory(this.router)(link)) {
          this.update();
        }
      });
    }
    // identical to regular routerLinkActive
    ngOnDestroy() {
      this.routerEventsSubscription.unsubscribe();
      this.linkInputChangesSubscription?.unsubscribe();
    }
    // custom implementation
    update() {
      if (!this.links || !this.router.navigated) return;
      queueMicrotask(() => {
        const hasActiveLinks = this.hasActiveLinks();
        const situationsToCheck = [{
          currentStateProp: '_isActive',
          newState: hasActiveLinks.isActive,
          className: this.linkParams.isActive
        }, {
          currentStateProp: '_isInActivePath',
          newState: hasActiveLinks.isInActivePath,
          className: this.linkParams.isInActivePath
        }].filter(({
          className
        }) => {
          // filter out situations without a className, these are irrelevant.
          return !!className;
        });
        // multiple situations may use the same class, resulting in conflicting addClass/removeClass calls.
        // for this reason, whenever SOMEthing changes, EVERY situation must be reapplied.
        const stateHasAnyChanges = situationsToCheck.some(({
          currentStateProp,
          newState
        }) => {
          return this[currentStateProp] !== newState;
        });
        if (!stateHasAnyChanges) return;
        const [activeSituations, inactiveSituations] = arraySplitByCondition(situationsToCheck, ({
          newState
        }) => {
          return newState === true;
        });
        inactiveSituations.forEach(({
          currentStateProp,
          className
        }) => {
          this[currentStateProp] = false;
          this.renderer.removeClass(this.element.nativeElement, className);
        });
        activeSituations.forEach(({
          currentStateProp,
          className
        }) => {
          this[currentStateProp] = true;
          this.renderer.addClass(this.element.nativeElement, className);
        });
        this.cdr.markForCheck();
        this.stateEmitter.emit({
          isActive: this._isActive,
          isInActivePath: this._isInActivePath
        });
      });
    }
    // check if any children are active
    hasActiveLinks() {
      const isActiveCheckFnExact = isActiveRouterLinkFactory(this.router, true);
      const isActiveCheckFn = isActiveRouterLinkFactory(this.router, false);
      if (this.routerLink) {
        // HostElement has a routerLink
        const isInActivePath = isActiveCheckFn(this.routerLink);
        const isActive = isActiveCheckFnExact(this.routerLink);
        return {
          isInActivePath,
          isActive
        };
      } else {
        // HostElement has no routerLink, search Content Dom for child routerLinks
        // const isInPath = this.link && isActiveCheckFnExact(this.link) 
        // 	|| this.linkWithHref && isActiveCheckFnExact(this.linkWithHref) 
        // 	|| this.links.some(isActiveCheckFnExact) 
        // 	|| this.linksWithHrefs.some(isActiveCheckFnExact);
        const isInActivePath = this.links.some(isActiveCheckFnExact) || this.linksWithHrefs.some(isActiveCheckFnExact);
        return {
          isInActivePath,
          isActive: false
        };
      }
    }
    static {
      this.ɵfac = function UiRouterLinkActivePathDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UiRouterLinkActivePathDirective)(i0.ɵɵdirectiveInject(i1.Router), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.RouterLink, 8));
      };
    }
    static {
      this.ɵdir = /*@__PURE__*/i0.ɵɵdefineDirective({
        type: UiRouterLinkActivePathDirective,
        selectors: [["", "routerLinkActivePath", ""]],
        contentQueries: function UiRouterLinkActivePathDirective_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, RouterLink, 5)(dirIndex, RouterLink, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.links = _t);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.linksWithHrefs = _t);
          }
        },
        inputs: {
          linkParams: [0, "routerLinkActivePath", "linkParams"]
        },
        outputs: {
          stateEmitter: "routerLinkActiveState"
        },
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return UiRouterLinkActivePathDirective;
})();